import { useState, useEffect, useRef } from 'react';
import { useUserAuth } from '@/providers/UserAuthProvider';
import { useAccount, useSignMessage } from 'wagmi';
import { useWeb3Modal } from '@web3modal/wagmi/react';
import { useSearchParams, useLocation } from 'react-router-dom';
import { doc, getDoc, setDoc, deleteDoc } from 'firebase/firestore';
import { initializeFirebase } from '@/lib/firebase';
import { Copy, Check, Users, DollarSign, TrendingUp, Wallet, LogIn, ChevronDown, ChevronUp, LogOut, Link as LinkIcon, Mail, X, User, RotateCcw, Palette } from 'lucide-react';
import { FaGoogle, FaCog, FaHandshake, FaDollarSign } from 'react-icons/fa';
import { FaXTwitter } from 'react-icons/fa6';
import { AuthModal } from '@/components/auth/AuthModal';
import SwatchPro from '@/components/SwatchPro';

export function AccountPage() {
  const { user, resendVerificationEmail, logout, reauthWithGoogle, reauthWithTwitter, reauthWithPassword, deleteAccount, linkGoogle, linkTwitter, linkEmailPassword, linkWallet, unlinkProvider, unlinkWallet, updateDisplayName, resetDisplayName } = useUserAuth();
  const { address, isConnected } = useAccount();
  const { signMessageAsync } = useSignMessage();
  const { open: openWeb3Modal } = useWeb3Modal();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const loadedForUidRef = useRef<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [dangerOpen, setDangerOpen] = useState(false);
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');

  // Referral states
  const [customReferralCode, setCustomReferralCode] = useState('');
  const [isEditingCode, setIsEditingCode] = useState(false);
  const [newReferralCode, setNewReferralCode] = useState('');
  const [codeError, setCodeError] = useState('');
  const [codeSaving, setCodeSaving] = useState(false);
  const [copiedLink, setCopiedLink] = useState(false);
  const [payoutAddress, setPayoutAddress] = useState('');
  const [useCustomAddress, setUseCustomAddress] = useState(false);
  const [customPayoutAddress, setCustomPayoutAddress] = useState('');
  const [payoutAddressError, setPayoutAddressError] = useState('');
  const [savingPayoutAddress, setSavingPayoutAddress] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [authOpen, setAuthOpen] = useState(false);
  const [deleteCardExpanded, setDeleteCardExpanded] = useState(false);
  const [linkAccountsExpanded, setLinkAccountsExpanded] = useState(false);
  const [linking, setLinking] = useState(false);
  const [linkMessage, setLinkMessage] = useState('');
  const [activeTab, setActiveTab] = useState<'settings' | 'referrals' | 'app-settings'>('settings');
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [linkEmail, setLinkEmail] = useState('');
  const [linkPassword, setLinkPassword] = useState('');
  const [linkedWallet, setLinkedWallet] = useState<string | null>(null);
  const [pendingWalletLink, setPendingWalletLink] = useState(false);
  const [displayNameExpanded, setDisplayNameExpanded] = useState(false);
  const [originalDisplayName, setOriginalDisplayName] = useState<string>('');
  const [newDisplayName, setNewDisplayName] = useState('');
  const [displayNameMessage, setDisplayNameMessage] = useState('');
  const [buttonLayout, setButtonLayout] = useState<'Top' | 'Bottom' | 'Left' | 'Right'>(() => {
    return (localStorage.getItem('buttonLayout') as 'Top' | 'Bottom' | 'Left' | 'Right') || 'Top';
  });

  // Set active tab from URL path
  useEffect(() => {
    const path = location.pathname;
    if (path.includes('/account/settings')) {
      setActiveTab('settings');
    } else if (path.includes('/account/referrals')) {
      setActiveTab('referrals');
    } else if (path.includes('/account/app-settings')) {
      setActiveTab('app-settings');
    }
  }, [location.pathname]);

  // Load referral data from Firestore - runs once per user
  useEffect(() => {
    // Only run when user is present
    if (!user?.uid) {
      return;
    }

    // Prevent re-running for the same user session
    if (loadedForUidRef.current === user.uid) {
      return;
    }

    // Mark immediately to prevent double-runs
    loadedForUidRef.current = user.uid;

    let isMounted = true;

    const loadReferralData = async () => {
      try {
        const { db } = await initializeFirebase();
        const userRef = doc(db, 'users', user.uid);
        const userDoc = await getDoc(userRef);

        if (!isMounted) return;

        if (userDoc.exists()) {
          const data = userDoc.data() as any;

          const fallbackCode = address ? address.slice(0, 8) : '';
          const nextCode = data.customReferralCode || fallbackCode;
          setCustomReferralCode(nextCode);

          // Load linked wallet
          if (data.linkedWallet) {
            setLinkedWallet(data.linkedWallet);
          }

          // Load original display name
          if (data.originalDisplayName) {
            setOriginalDisplayName(data.originalDisplayName);
          }

          if (data.payoutAddress) {
            setPayoutAddress(data.payoutAddress);
            if (address && data.payoutAddress !== address) {
              setUseCustomAddress(true);
              setCustomPayoutAddress(data.payoutAddress);
            } else if (address && data.payoutAddress === address) {
              setUseCustomAddress(false);
            }
          } else if (address) {
            setPayoutAddress(address);
          }
        } else {
          // No user doc yet; use wallet address as sensible defaults if available
          if (address) {
            setPayoutAddress(address);
            const nextCode = address.slice(0, 8);
            setCustomReferralCode(nextCode);
          }
        }
      } catch (error) {
        if (isMounted) {
          console.error('Error loading referral data:', error);
        }
      }
    };

    loadReferralData();

    return () => {
      isMounted = false;
    };
  }, [user?.uid, address]);

  // Separate effect to reset ref on sign out
  useEffect(() => {
    if (!user?.uid) {
      loadedForUidRef.current = null;
    }
  }, [user?.uid]);

  // Auto-link wallet when connection is detected after clicking Link Wallet button
  useEffect(() => {
    if (pendingWalletLink && isConnected && address) {
      setPendingWalletLink(false);
      // Automatically proceed with linking
      (async () => {
        try {
          setLinking(true);
          setLinkMessage('');
          const nonce = Date.now();
          const message = `Link Wallet to MdbApp Account\nAddress: ${address}\nNonce: ${nonce}`;
          const signature = await signMessageAsync({ message });
          await linkWallet(address, signature, message);
          setLinkedWallet(address.toLowerCase());
          setLinkMessage('✅ Wallet linked successfully!');
          setTimeout(() => setLinkMessage(''), 3000);
        } catch (error: any) {
          console.error('Auto-link Wallet error:', error);
          setLinkMessage(`❌ Failed to link wallet: ${error.message}`);
        } finally {
          setLinking(false);
        }
      })();
    }
  }, [pendingWalletLink, isConnected, address, signMessageAsync, linkWallet]);

  // Early return AFTER all hooks
  if (!user) return (
    <>
      <div className="max-w-3xl mx-auto px-4 py-8 min-h-[70vh] flex flex-col items-center justify-center">
        <div className="text-center space-y-8 max-w-2xl">
          {/* Header */}
          <div className="space-y-4">
            <h1 className="text-4xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-blue-600 to-purple-600">
              Your Account Dashboard
            </h1>
            <p className="text-lg text-muted-foreground">
              Sign in to access your account settings and referral program
            </p>
          </div>

          {/* Features Grid */}
          <div className="grid sm:grid-cols-2 gap-4 text-left">
            <div className="border rounded-lg p-6 bg-card hover:border-blue-500/50 transition-colors">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-full bg-blue-500/10 flex items-center justify-center">
                  <FaCog className="w-5 h-5 text-blue-500" />
                </div>
                <h3 className="font-semibold text-lg">Account Settings</h3>
              </div>
              <p className="text-sm text-muted-foreground">
                Manage your profile, link multiple sign-in methods, and control your account preferences
              </p>
            </div>

            <div className="border rounded-lg p-6 bg-card hover:border-green-500/50 transition-colors">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-full bg-green-500/10 flex items-center justify-center">
                  <DollarSign className="w-5 h-5 text-green-500" />
                </div>
                <h3 className="font-semibold text-lg">Referral Program</h3>
              </div>
              <p className="text-sm text-muted-foreground">
                Earn 50% commission on every token created by your referrals - share your link and start earning!
              </p>
            </div>

            <div className="border rounded-lg p-6 bg-card hover:border-purple-500/50 transition-colors">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-full bg-purple-500/10 flex items-center justify-center">
                  <Users className="w-5 h-5 text-purple-500" />
                </div>
                <h3 className="font-semibold text-lg">Track Referrals</h3>
              </div>
              <p className="text-sm text-muted-foreground">
                Monitor your referral stats, earnings, and see how many friends have signed up
              </p>
            </div>

            <div className="border rounded-lg p-6 bg-card hover:border-yellow-500/50 transition-colors">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-full bg-yellow-500/10 flex items-center justify-center">
                  <Wallet className="w-5 h-5 text-yellow-500" />
                </div>
                <h3 className="font-semibold text-lg">Wallet Integration</h3>
              </div>
              <p className="text-sm text-muted-foreground">
                Connect your wallet for seamless authentication and receive your referral earnings directly
              </p>
            </div>
          </div>

          {/* CTA Button */}
          <div className="pt-4">
            <button
              onClick={() => setAuthOpen(true)}
              className="inline-flex items-center gap-2 rounded-lg bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 px-8 py-4 text-white font-semibold text-lg transition-all hover:shadow-lg hover:scale-105"
            >
              <LogIn className="w-6 h-6" />
              Sign In or Create Account
            </button>
            <p className="text-sm text-muted-foreground mt-4">
              Sign in with Google, X (Twitter), Email, or your Wallet
            </p>
          </div>
        </div>
      </div>
      <AuthModal open={authOpen} onOpenChange={setAuthOpen} />
    </>
  );

  // Calculate values after hooks but before JSX
  const provider = user.providerData?.[0];
  const displayProvider = provider?.providerId === 'twitter.com' ? 'x.com' : (provider?.providerId || 'password');
  
  // Check if user is verified (OAuth providers are auto-verified)
  const isVerified = user.emailVerified || 
    provider?.providerId === 'google.com' || 
    provider?.providerId === 'twitter.com';

  // Calculate account age
  const accountCreated = user.metadata?.creationTime ? new Date(user.metadata.creationTime) : null;
  const accountAge = accountCreated 
    ? Math.floor((Date.now() - accountCreated.getTime()) / (1000 * 60 * 60 * 24))
    : null;

  // Calculate days since last sign-in
  const lastSignIn = user.metadata?.lastSignInTime ? new Date(user.metadata.lastSignInTime) : null;
  const daysSinceLogin = lastSignIn
    ? Math.floor((Date.now() - lastSignIn.getTime()) / (1000 * 60 * 60 * 24))
    : null;

  // Save custom referral code
  const handleSaveReferralCode = async () => {
    if (!user?.uid) {
      setCodeError('Please sign in to save a custom code');
      return;
    }
    if (!newReferralCode.trim()) {
      setCodeError('Please enter a referral code');
      return;
    }
    const codeRegex = /^[a-zA-Z0-9_-]{3,20}$/;
    if (!codeRegex.test(newReferralCode)) {
      setCodeError('Code must be 3-20 characters (letters, numbers, _ or - only)');
      return;
    }
    try {
      setCodeSaving(true);
      setCodeError('');
      const { db } = await initializeFirebase();
      const normalizedCode = newReferralCode.toLowerCase();
      const codeRef = doc(db, 'referralCodes', normalizedCode);
      const codeDoc = await getDoc(codeRef);
      if (codeDoc.exists() && codeDoc.data().userId !== user.uid) {
        setCodeError('This code is already taken. Please choose another.');
        setCodeSaving(false);
        return;
      }
      const userRef = doc(db, 'users', user.uid);
      await setDoc(userRef, {
        customReferralCode: normalizedCode,
        updatedAt: new Date().toISOString(),
        email: user.email || null,
        uid: user.uid
      }, { merge: true });
      await setDoc(doc(db, 'referralCodes', normalizedCode), {
        userId: user.uid,
        createdAt: new Date().toISOString()
      });
      if (customReferralCode && customReferralCode !== normalizedCode) {
        try {
          await deleteDoc(doc(db, 'referralCodes', customReferralCode));
        } catch (err) {
          console.log('Old code deletion skipped:', err);
        }
      }
      setCustomReferralCode(normalizedCode);
      setIsEditingCode(false);
      setNewReferralCode('');
      setSuccessMessage('✅ Custom referral code saved successfully!');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error: any) {
      console.error('Error saving referral code:', error);
      if (error.code === 'permission-denied') {
        setCodeError('Permission denied. Please check Firestore rules.');
      } else {
        setCodeError(`Failed to save: ${error.message || 'Please try again'}`);
      }
    } finally {
      setCodeSaving(false);
    }
  };

  // Save payout address
  const handleSavePayoutAddress = async () => {
    if (!user?.uid) {
      setPayoutAddressError('Please sign in to save payout address');
      return;
    }
    const addressToSave = useCustomAddress ? customPayoutAddress : address;
    if (!addressToSave || !addressToSave.trim()) {
      setPayoutAddressError('Please enter a valid address');
      return;
    }
    try {
      setSavingPayoutAddress(true);
      setPayoutAddressError('');
      const { db } = await initializeFirebase();
      const userRef = doc(db, 'users', user.uid);
      await setDoc(userRef, {
        payoutAddress: addressToSave,
        payoutAddressType: useCustomAddress ? 'custom' : 'wallet',
        payoutAddressUpdatedAt: new Date().toISOString()
      }, { merge: true });
      setPayoutAddress(addressToSave);
      setSuccessMessage('✅ Payout address saved successfully!');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error: any) {
      console.error('Error saving payout address:', error);
      if (error.code === 'permission-denied') {
        setPayoutAddressError('Permission denied. Please check Firestore rules.');
      } else {
        setPayoutAddressError(`Failed to save: ${error.message || 'Please try again'}`);
      }
    } finally {
      setSavingPayoutAddress(false);
    }
  };

  const handleCopyLink = () => {
    const link = `https://mdbapp.io?ref=${customReferralCode || user?.uid?.slice(0, 8) || 'USER123'}`;
    navigator.clipboard.writeText(link);
    setCopiedLink(true);
    setTimeout(() => setCopiedLink(false), 2000);
  };

  // Account linking handlers
  const handleLinkGoogle = async () => {
    try {
      setLinking(true);
      setLinkMessage('');
      await linkGoogle();
      setLinkMessage('✅ Google account linked successfully!');
      setTimeout(() => setLinkMessage(''), 3000);
    } catch (error: any) {
      console.error('Link Google error:', error);
      setLinkMessage(`❌ Failed to link Google: ${error.message}`);
    } finally {
      setLinking(false);
    }
  };

  const handleLinkTwitter = async () => {
    try {
      setLinking(true);
      setLinkMessage('');
      await linkTwitter();
      setLinkMessage('✅ X (Twitter) account linked successfully!');
      setTimeout(() => setLinkMessage(''), 3000);
    } catch (error: any) {
      console.error('Link Twitter error:', error);
      setLinkMessage(`❌ Failed to link X: ${error.message}`);
    } finally {
      setLinking(false);
    }
  };

  const handleLinkEmailPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setLinking(true);
      setLinkMessage('');
      await linkEmailPassword(linkEmail.trim(), linkPassword);
      setLinkMessage('✅ Email/Password linked successfully!');
      setLinkEmail('');
      setLinkPassword('');
      setShowEmailForm(false);
      setTimeout(() => setLinkMessage(''), 3000);
    } catch (error: any) {
      console.error('Link Email/Password error:', error);
      setLinkMessage(`❌ Failed to link Email/Password: ${error.message}`);
    } finally {
      setLinking(false);
    }
  };

  const handleLinkWallet = async () => {
    if (!isConnected || !address) {
      // Open wallet connect modal automatically and set pending flag
      try {
        setPendingWalletLink(true);
        await openWeb3Modal({ view: 'Connect' });
        setLinkMessage('Connecting wallet...');
      } catch (modalError) {
        console.error('Failed to open wallet modal:', modalError);
        setPendingWalletLink(false);
        setLinkMessage('❌ Please connect your wallet using the header button.');
      }
      return;
    }
    try {
      setLinking(true);
      setLinkMessage('');
      const nonce = Date.now();
      const message = `Link Wallet to MdbApp Account\nAddress: ${address}\nNonce: ${nonce}`;
      const signature = await signMessageAsync({ message });
      await linkWallet(address, signature, message);
      setLinkedWallet(address.toLowerCase());
      setLinkMessage('✅ Wallet linked successfully!');
      setTimeout(() => setLinkMessage(''), 3000);
    } catch (error: any) {
      console.error('Link Wallet error:', error);
      setLinkMessage(`❌ Failed to link wallet: ${error.message}`);
    } finally {
      setLinking(false);
    }
  };

  const handleUnlinkProvider = async (providerId: string, providerName: string) => {
    try {
      setLinking(true);
      setLinkMessage('');
      await unlinkProvider(providerId);
      setLinkMessage(`✅ ${providerName} unlinked successfully!`);
      setTimeout(() => setLinkMessage(''), 3000);
    } catch (error: any) {
      console.error('Unlink provider error:', error);
      setLinkMessage(`❌ ${error.message}`);
    } finally {
      setLinking(false);
    }
  };

  const handleUnlinkWallet = async () => {
    try {
      setLinking(true);
      setLinkMessage('');
      await unlinkWallet();
      setLinkedWallet(null);
      setLinkMessage('✅ Wallet unlinked successfully!');
      setTimeout(() => setLinkMessage(''), 3000);
    } catch (error: any) {
      console.error('Unlink wallet error:', error);
      setLinkMessage(`❌ Failed to unlink wallet: ${error.message}`);
    } finally {
      setLinking(false);
    }
  };

  const handleUpdateDisplayName = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newDisplayName.trim()) {
      setDisplayNameMessage('❌ Display name cannot be empty');
      return;
    }
    try {
      setBusy(true);
      setDisplayNameMessage('');
      await updateDisplayName(newDisplayName.trim());
      setDisplayNameMessage('✅ Display name updated successfully!');
      setNewDisplayName('');
      setTimeout(() => setDisplayNameMessage(''), 3000);
    } catch (error: any) {
      console.error('Update display name error:', error);
      setDisplayNameMessage(`❌ Failed to update display name: ${error.message}`);
    } finally {
      setBusy(false);
    }
  };

  const handleResetDisplayName = async () => {
    try {
      setBusy(true);
      setDisplayNameMessage('');
      await resetDisplayName();
      setDisplayNameMessage('✅ Display name reset to original!');
      setTimeout(() => setDisplayNameMessage(''), 3000);
    } catch (error: any) {
      console.error('Reset display name error:', error);
      setDisplayNameMessage(`❌ Failed to reset display name: ${error.message}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <div className="max-w-3xl mx-auto px-4 py-8 space-y-6 text-center relative z-10">
        <div className="flex flex-col items-center gap-4">
        <img
          src={user.photoURL || '/Dynamic Header Logos/MdbApp.io Default Logo.png'}
          alt="avatar"
          className="w-16 h-16 rounded-full border"
          onError={(e: any) => { e.currentTarget.src = '/Dynamic Header Logos/MdbApp.io Default Logo.png'; }}
        />
        <div>
          <div className="flex items-center justify-center gap-2">
            <h1 className="text-2xl font-bold">{user.displayName || 'Your Account'}</h1>
            {isVerified && (
              <img 
                src="/Custom Verified Badge MdbApp.io.png" 
                alt="Verified" 
                className="w-6 h-6"
                title="Verified account"
              />
            )}
          </div>
          <div className="text-sm text-muted-foreground">{user.email}</div>
          <div className="flex items-center justify-center gap-3 mt-2 text-xs text-muted-foreground">
            {accountAge !== null && (
              <span>Member for {accountAge} {accountAge === 1 ? 'day' : 'days'}</span>
            )}
            {daysSinceLogin !== null && (
              <span>• {daysSinceLogin === 0 ? 'Active today' : `Last active ${daysSinceLogin} ${daysSinceLogin === 1 ? 'day' : 'days'} ago`}</span>
            )}
          </div>
        </div>
      </div>

      {/* Tab Buttons */}
      <div className="flex gap-2 border-b border-border">
        <button
          onClick={() => setActiveTab('settings')}
          className={`flex-1 px-4 py-3 font-semibold transition-colors inline-flex items-center justify-center gap-2 ${
            activeTab === 'settings'
              ? 'text-[var(--color-header-accent,#0A74FF)] border-b-2 border-[var(--color-header-accent,#0A74FF)]'
              : 'text-muted-foreground hover:text-foreground'
          }`}
        >
          <FaCog className={activeTab === 'settings' ? '' : 'dark:text-gray-400 text-gray-600'} />
          Account Settings
        </button>
        <button
          onClick={() => setActiveTab('referrals')}
          className={`flex-1 px-4 py-3 font-semibold transition-colors inline-flex items-center justify-center gap-2 ${
            activeTab === 'referrals'
              ? 'text-[var(--color-header-accent,#0A74FF)] border-b-2 border-[var(--color-header-accent,#0A74FF)]'
              : 'text-muted-foreground hover:text-foreground'
          }`}
        >
          <div className="relative inline-flex items-center">
            <FaHandshake className={activeTab === 'referrals' ? '' : 'text-[var(--color-header-accent,#0A74FF)]'} />
            <FaDollarSign className={`absolute -top-1 -right-2 text-xs ${activeTab === 'referrals' ? '' : 'text-green-500 animate-spin'}`} style={{ animationDuration: '3s' }} />
          </div>
          Referrals
        </button>
        <button
          onClick={() => setActiveTab('app-settings')}
          className={`flex-1 px-4 py-3 font-semibold transition-colors inline-flex items-center justify-center gap-2 ${
            activeTab === 'app-settings'
              ? 'text-[var(--color-header-accent,#0A74FF)] border-b-2 border-[var(--color-header-accent,#0A74FF)]'
              : 'text-muted-foreground hover:text-foreground'
          }`}
        >
          <Palette className={activeTab === 'app-settings' ? '' : 'dark:text-gray-400 text-gray-600'} />
          App Settings
        </button>
      </div>

      {/* Account Settings Tab */}
      {activeTab === 'settings' && (
        <>
      <div className="grid sm:grid-cols-2 gap-4">
        <div className="border rounded-lg p-4 text-center">
          <div className="font-semibold mb-2">Authentication</div>
          <div className="text-sm space-y-1">
            <div><span className="text-muted-foreground">UID:</span> {user.uid}</div>
            <div><span className="text-muted-foreground">Provider:</span> {displayProvider}</div>
            <div className="flex items-center justify-center gap-1">
              <span className="text-muted-foreground">Verified:</span> 
              {isVerified ? (
                <>
                  <span>Yes</span>
                  <img 
                    src="/Custom Verified Badge MdbApp.io.png" 
                    alt="Verified" 
                    className="w-4 h-4"
                    title="Verified account"
                  />
                </>
              ) : (
                <span>No</span>
              )}
            </div>
            <div><span className="text-muted-foreground">Created:</span> {user.metadata?.creationTime}</div>
            <div><span className="text-muted-foreground">Last sign-in:</span> {user.metadata?.lastSignInTime}</div>
          </div>
          {!isVerified && provider?.providerId === 'password' && (
            <button
              className="mt-3 inline-flex items-center gap-2 rounded border-2 border-[var(--color-header-accent,#0A74FF)] px-3 py-1.5 text-sm font-semibold hover:bg-accent"
              onClick={async () => { try { await resendVerificationEmail(); } catch (e) { console.error(e); } }}
            >
              Resend verification email
            </button>
          )}
        </div>

        <div className="border rounded-lg p-4 text-center">
          <div className="font-semibold mb-2">Wallet</div>
          <div className="text-sm space-y-1">
            <div><span className="text-muted-foreground">Connected:</span> {isConnected ? 'Yes' : 'No'}</div>
            <div><span className="text-muted-foreground">Address:</span> {address || '—'}</div>
          </div>
        </div>
      </div>

      {/* Link Accounts Section */}
      <div className="border rounded-lg p-4 border-border bg-card">
        <div 
          className="flex items-center justify-center gap-2 cursor-pointer relative"
          onClick={() => setLinkAccountsExpanded(!linkAccountsExpanded)}
        >
          <LinkIcon className="w-5 h-5 text-[var(--color-header-accent,#0A74FF)]" />
          <h3 className="font-semibold">Link Accounts</h3>
          <LinkIcon className="w-5 h-5 text-[var(--color-header-accent,#0A74FF)]" />
          <button className="absolute right-0 text-muted-foreground hover:text-foreground">
            {linkAccountsExpanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
          </button>
        </div>

        {linkAccountsExpanded && (
          <div className="mt-4 space-y-4 text-center">
            <p className="text-sm text-muted-foreground">
              Link multiple sign-in methods to your account for easier access. You can sign in with any linked method.
            </p>

            {/* Currently Linked */}
            <div>
              <h4 className="text-sm font-medium mb-2">Currently Linked:</h4>
              <div className="flex flex-wrap gap-2 justify-center">
                {user.providerData?.map((provider, index) => (
                  <div key={index} className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-accent/10 border border-[var(--color-header-accent,#0A74FF)] text-[var(--color-header-accent,#0A74FF)] text-sm group relative">
                    {provider.providerId === 'google.com' && <><FaGoogle /> Google</>}
                    {provider.providerId === 'twitter.com' && <><FaXTwitter /> X (Twitter)</>}
                    {provider.providerId === 'password' && <><Mail className="w-4 h-4" /> Email/Password</>}
                    <button
                      onClick={() => handleUnlinkProvider(
                        provider.providerId,
                        provider.providerId === 'google.com' ? 'Google' : 
                        provider.providerId === 'twitter.com' ? 'X (Twitter)' : 
                        'Email/Password'
                      )}
                      disabled={linking}
                      className="ml-1 p-0.5 rounded-full hover:bg-red-500/20 text-red-500 disabled:opacity-50"
                      title="Unlink"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
                {linkedWallet && (
                  <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-accent/10 border border-[var(--color-header-accent,#0A74FF)] text-[var(--color-header-accent,#0A74FF)] text-sm">
                    <Wallet className="w-4 h-4" /> Wallet ({linkedWallet.slice(0, 6)}...{linkedWallet.slice(-4)})
                    <button
                      onClick={handleUnlinkWallet}
                      disabled={linking}
                      className="ml-1 p-0.5 rounded-full hover:bg-red-500/20 text-red-500 disabled:opacity-50"
                      title="Unlink Wallet"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Link Buttons */}
            <div className="grid grid-cols-2 gap-3">
              {!user.providerData?.some(p => p.providerId === 'google.com') && (
                <button 
                  onClick={handleLinkGoogle}
                  disabled={linking}
                  className="inline-flex items-center justify-center gap-2 rounded border-2 border-[var(--color-header-accent,#0A74FF)] px-4 py-2 text-sm font-semibold hover:bg-accent disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <FaGoogle /> {linking ? 'Linking...' : 'Link Google'}
                </button>
              )}

              {!user.providerData?.some(p => p.providerId === 'twitter.com') && (
                <button 
                  onClick={handleLinkTwitter}
                  disabled={linking}
                  className="inline-flex items-center justify-center gap-2 rounded border-2 border-[var(--color-header-accent,#0A74FF)] px-4 py-2 text-sm font-semibold hover:bg-accent disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <FaXTwitter /> {linking ? 'Linking...' : 'Link X (Twitter)'}
                </button>
              )}

              {!user.providerData?.some(p => p.providerId === 'password') && (
                <button 
                  onClick={() => setShowEmailForm(!showEmailForm)}
                  disabled={linking}
                  className="inline-flex items-center justify-center gap-2 rounded border-2 border-[var(--color-header-accent,#0A74FF)] px-4 py-2 text-sm font-semibold hover:bg-accent disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Mail className="w-4 h-4" /> {showEmailForm ? 'Cancel' : 'Link Email/Password'}
                </button>
              )}

              {!linkedWallet && (
                <button 
                  onClick={handleLinkWallet}
                  disabled={linking}
                  className="inline-flex items-center justify-center gap-2 rounded border-2 border-[var(--color-header-accent,#0A74FF)] px-4 py-2 text-sm font-semibold hover:bg-accent disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Wallet className="w-4 h-4" /> {linking ? 'Linking...' : 'Link Wallet'}
                </button>
              )}
            </div>

            {/* Email/Password Form */}
            {showEmailForm && !user.providerData?.some(p => p.providerId === 'password') && (
              <form onSubmit={handleLinkEmailPassword} className="space-y-3">
                <input
                  type="email"
                  value={linkEmail}
                  onChange={(e) => setLinkEmail(e.target.value)}
                  placeholder="Email"
                  required
                  className="w-full rounded border bg-background px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[var(--color-header-accent,#0A74FF)]"
                />
                <input
                  type="password"
                  value={linkPassword}
                  onChange={(e) => setLinkPassword(e.target.value)}
                  placeholder="Password (min 6 characters)"
                  required
                  minLength={6}
                  className="w-full rounded border bg-background px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[var(--color-header-accent,#0A74FF)]"
                />
                <button
                  type="submit"
                  disabled={linking}
                  className="w-full inline-flex items-center justify-center gap-2 rounded border-2 border-[var(--color-header-accent,#0A74FF)] px-4 py-2 text-sm font-semibold hover:bg-accent disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {linking ? 'Linking...' : 'Link Email/Password'}
                </button>
              </form>
            )}

            {/* Link status message */}
            {linkMessage && (
              <div className={`text-sm rounded px-3 py-2 text-center ${
                linkMessage.includes('✅') 
                  ? 'text-[var(--color-header-accent,#0A74FF)] bg-accent/10 border border-[var(--color-header-accent,#0A74FF)]' 
                  : 'text-red-600 bg-red-500/10 border border-red-500/30'
              }`}>
                {linkMessage}
              </div>
            )}

            {user.providerData?.length >= 2 && isConnected && !linkMessage && (
              <div className="text-sm text-[var(--color-header-accent,#0A74FF)] bg-accent/10 border border-[var(--color-header-accent,#0A74FF)] rounded px-3 py-2 text-center">
                ✓ All available methods are linked to your account!
              </div>
            )}
          </div>
        )}
      </div>

      {/* Display Name Card */}
      <div className="rounded-lg border-2 border-[var(--color-header-accent,#0A74FF)] bg-card p-4">
        <div 
          className="flex items-center justify-center gap-2 cursor-pointer relative"
          onClick={() => setDisplayNameExpanded(!displayNameExpanded)}
        >
          <User className="w-5 h-5 text-[var(--color-header-accent,#0A74FF)]" />
          <h3 className="font-semibold">Customize Display Name</h3>
          <User className="w-5 h-5 text-[var(--color-header-accent,#0A74FF)]" />
          <button className="absolute right-0 text-muted-foreground hover:text-foreground">
            {displayNameExpanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
          </button>
        </div>

        {displayNameExpanded && (
          <div className="mt-4 space-y-4 text-center">
            <p className="text-sm text-muted-foreground">
              Customize how your name appears throughout the app. You can always reset it to the original.
            </p>

            {/* Current Display Name */}
            <div>
              <h4 className="text-sm font-medium mb-2">Current Display Name:</h4>
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-accent/10 border border-[var(--color-header-accent,#0A74FF)] text-[var(--color-header-accent,#0A74FF)] text-sm">
                {user.displayName || 'Not set'}
              </div>
            </div>

            {/* Original Display Name (if different) */}
            {originalDisplayName && originalDisplayName !== user.displayName && (
              <div>
                <h4 className="text-sm font-medium mb-2">Original Display Name:</h4>
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gray-500/10 border border-gray-500/30 text-gray-400 text-sm">
                  {originalDisplayName}
                </div>
              </div>
            )}

            {/* Update Display Name Form */}
            <form onSubmit={handleUpdateDisplayName} className="space-y-3">
              <input
                type="text"
                value={newDisplayName}
                onChange={(e) => setNewDisplayName(e.target.value)}
                placeholder="Enter new display name"
                style={{
                  borderColor: 'var(--color-border, #333333)',
                  color: 'var(--color-text, #e5e5e5)'
                }}
                className="w-full rounded border-2 bg-background px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[var(--color-header-accent,#0A74FF)]"
              />
              <div className="flex gap-3">
                <button
                  type="submit"
                  disabled={busy || !newDisplayName.trim()}
                  className="flex-1 inline-flex items-center justify-center gap-2 rounded border-2 border-[var(--color-header-accent,#0A74FF)] px-4 py-2 text-sm font-semibold hover:bg-accent disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <User className="w-4 h-4" /> {busy ? 'Updating...' : 'Update Name'}
                </button>
                {originalDisplayName && (
                  <button
                    type="button"
                    onClick={handleResetDisplayName}
                    disabled={busy}
                    className="flex-1 inline-flex items-center justify-center gap-2 rounded border-2 border-gray-500 px-4 py-2 text-sm font-semibold hover:bg-gray-500/10 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <RotateCcw className="w-4 h-4" /> {busy ? 'Resetting...' : 'Reset to Original'}
                  </button>
                )}
              </div>
            </form>

            {/* Display name status message */}
            {displayNameMessage && (
              <div className={`text-sm rounded px-3 py-2 text-center ${
                displayNameMessage.includes('✅') 
                  ? 'text-[var(--color-header-accent,#0A74FF)] bg-accent/10 border border-[var(--color-header-accent,#0A74FF)]' 
                  : 'text-red-600 bg-red-500/10 border border-red-500/30'
              }`}>
                {displayNameMessage}
              </div>
            )}
          </div>
        )}
      </div>

      <div>
        <button
          className="inline-flex items-center gap-2 rounded border-2 border-[var(--color-header-accent,#0A74FF)] text-[var(--color-header-accent,#0A74FF)] px-3 py-1.5 text-sm font-semibold hover:bg-accent"
          onClick={async () => { try { await logout(); } catch (e) { console.error(e); } }}
        >
          <LogOut className="w-4 h-4" />
          Sign out
        </button>
      </div>

      <div className="border rounded-lg p-4 border-red-500/50 bg-red-500/5">
        <div className="flex items-center justify-between cursor-pointer" onClick={() => setDeleteCardExpanded(!deleteCardExpanded)}>
          <div className="flex-1 text-center">
            <div className="font-semibold text-red-600">Delete Account</div>
            <div className="text-sm text-muted-foreground">This will permanently delete your account and cannot be undone.</div>
          </div>
          <button className="text-red-600 hover:text-red-700">
            {deleteCardExpanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
          </button>
        </div>
        {deleteCardExpanded && (
          <div className="mt-4 text-center">
            {error && (
              <div className="mb-2 text-sm text-red-600 bg-red-500/10 border border-red-500/30 rounded px-3 py-2">{error}</div>
            )}
            {!dangerOpen ? (
              <button
                className="inline-flex items-center gap-2 rounded border-2 border-red-600 text-red-600 px-3 py-1.5 text-sm font-semibold hover:bg-red-600/10"
                onClick={() => { setDangerOpen(true); setError(''); }}
              >
                I understand, delete my account
              </button>
            ) : (
          <div className="mt-3 grid gap-2 justify-items-center">
            <div className="text-sm">Re-authenticate to continue:</div>
            <div className="flex flex-wrap gap-2">
              <button
                disabled={busy}
                className="inline-flex items-center gap-2 rounded border px-3 py-1.5 text-sm font-semibold hover:bg-accent"
                onClick={async () => {
                  try {
                    setBusy(true); setError('');
                    await reauthWithGoogle();
                    await deleteAccount();
                  } catch (e: any) {
                    setError(e?.message || 'Re-auth with Google failed');
                  } finally { setBusy(false); }
                }}
              >Re-auth with Google</button>
              <button
                disabled={busy}
                className="inline-flex items-center gap-2 rounded border px-3 py-1.5 text-sm font-semibold hover:bg-accent"
                onClick={async () => {
                  try {
                    setBusy(true); setError('');
                    await reauthWithTwitter();
                    await deleteAccount();
                  } catch (e: any) {
                    setError(e?.message || 'Re-auth with X failed');
                  } finally { setBusy(false); }
                }}
              >Re-auth with X</button>
            </div>
            <div className="grid sm:grid-cols-[1fr_auto] gap-2 items-center max-w-md w-full">
              <input
                type="password"
                placeholder="Current password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                className="w-full rounded border bg-background px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#0A74FF]"
              />
              <button
                disabled={busy}
                className="inline-flex items-center gap-2 rounded border px-3 py-1.5 text-sm font-semibold hover:bg-accent"
                onClick={async () => {
                  try {
                    setBusy(true); setError('');
                    await reauthWithPassword(password);
                    await deleteAccount();
                  } catch (e: any) {
                    setError(e?.message || 'Password re-auth failed');
                  } finally { setBusy(false); }
                }}
              >Confirm delete</button>
            </div>
          </div>
            )}
          </div>
        )}
      </div>
        </>
      )}

      {/* Referrals Tab */}
      {activeTab === 'referrals' && (
      <>
          {/* Stats Grid */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="border rounded-lg p-4 text-center">
              <Users className="w-8 h-8 mx-auto mb-2 text-[var(--color-header-accent,#0A74FF)]" />
              <div className="text-2xl font-bold">0</div>
              <div className="text-sm text-muted-foreground">Friends Referred</div>
              <div className="text-xs text-muted-foreground mt-1">0 signed up</div>
            </div>

            <div className="border rounded-lg p-4 text-center">
              <DollarSign className="w-8 h-8 mx-auto mb-2 text-green-500" />
              <div className="text-2xl font-bold">$0</div>
              <div className="text-sm text-muted-foreground">Earnings</div>
              <div className="text-xs text-muted-foreground mt-1">$0 this month</div>
            </div>

            <div className="border rounded-lg p-4 text-center">
              <TrendingUp className="w-8 h-8 mx-auto mb-2 text-yellow-500" />
              <div className="text-2xl font-bold">0</div>
              <div className="text-sm text-muted-foreground">Tokens Created</div>
              <div className="text-xs text-muted-foreground mt-1">By your referrals</div>
            </div>

            <div className="border rounded-lg p-4 text-center">
              <Wallet className="w-8 h-8 mx-auto mb-2 text-purple-500" />
              <div className="text-2xl font-bold">$0</div>
              <div className="text-sm text-muted-foreground">Available Balance</div>
              <div className="text-xs text-muted-foreground mt-1">Ready to withdraw</div>
            </div>
          </div>

          {/* My Referral Link Card */}
          <div className="border rounded-lg p-6 bg-card">
            <h2 className="text-xl font-semibold mb-4">My Referral Link</h2>
            <div className="space-y-4">
              <div>
                <label className="text-sm text-muted-foreground mb-2 block">Share this link to earn rewards:</label>
                <div className="flex gap-2">
                  <div className="flex-1 bg-muted/50 border rounded px-3 py-2 text-sm font-mono break-all">
                    https://mdbapp.io?ref={customReferralCode || user?.uid?.slice(0, 8) || 'USER123'}
                  </div>
                  <button
                    onClick={handleCopyLink}
                    className="inline-flex items-center gap-2 rounded border-2 border-[var(--color-header-accent,#0A74FF)] px-4 py-2 text-sm font-semibold hover:bg-accent whitespace-nowrap"
                  >
                    {copiedLink ? <><Check className="w-4 h-4" /> Copied!</> : <><Copy className="w-4 h-4" /> Copy Link</>}
                  </button>
                </div>
              </div>

              {/* Custom Code Section */}
              <div className="border-t pt-4">
                {!isEditingCode ? (
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="text-sm text-muted-foreground">Your referral code: </span>
                      <span className="font-mono font-semibold">{customReferralCode || user?.uid?.slice(0, 8) || 'USER123'}</span>
                    </div>
                    <button
                      onClick={() => {
                        setIsEditingCode(true);
                        setNewReferralCode(customReferralCode || '');
                        setCodeError('');
                      }}
                      className="text-sm text-[var(--color-header-accent,#0A74FF)] hover:underline"
                    >
                      {customReferralCode ? 'Change Code' : 'Create Custom Code'}
                    </button>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <input
                      type="text"
                      value={newReferralCode}
                      onChange={(e) => {
                        setNewReferralCode(e.target.value);
                        setCodeError('');
                      }}
                      placeholder="Enter custom code (e.g., myname)"
                      maxLength={20}
                      className="w-full rounded border bg-background px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[var(--color-header-accent,#0A74FF)]"
                    />
                    <div className="flex gap-2">
                      <button
                        onClick={handleSaveReferralCode}
                        disabled={codeSaving}
                        className="inline-flex items-center gap-2 rounded border-2 border-[var(--color-header-accent,#0A74FF)] px-3 py-1.5 text-sm font-semibold hover:bg-accent"
                      >
                        {codeSaving ? 'Saving...' : 'Save'}
                      </button>
                      <button
                        onClick={() => {
                          setIsEditingCode(false);
                          setNewReferralCode('');
                          setCodeError('');
                        }}
                        className="inline-flex items-center gap-2 rounded border px-3 py-1.5 text-sm font-semibold hover:bg-accent"
                      >
                        Cancel
                      </button>
                    </div>
                    {codeError && <div className="text-sm text-red-600">{codeError}</div>}
                  </div>
                )}
              </div>

              <div className="bg-green-500/10 border border-green-500/30 rounded px-3 py-2 text-sm text-green-600">
                💰 Earn 50% commission on every token created by your referrals!
              </div>
            </div>
          </div>

          {/* Withdraw Earnings Card */}
          <div className="border rounded-lg p-6 bg-card">
            <h2 className="text-xl font-semibold mb-2">💰 Withdraw Your Earnings</h2>
            <p className="text-sm text-muted-foreground mb-4">
              Minimum withdrawal: <strong>$10</strong> • No fees • Instant to your wallet
            </p>

            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium mb-2 block">Payout Address:</label>
                <div className="space-y-3">
                  <label className="flex items-start gap-3 p-3 border rounded cursor-pointer hover:bg-accent/50">
                    <input
                      type="radio"
                      name="payout-type"
                      checked={!useCustomAddress}
                      onChange={() => {
                        setUseCustomAddress(false);
                        setPayoutAddressError('');
                        if (address) setPayoutAddress(address);
                      }}
                      disabled={!isConnected}
                      className="mt-1"
                    />
                    <div className="flex-1">
                      <div className="font-medium text-sm">Connected Wallet</div>
                      {isConnected ? (
                        <div className="text-xs text-muted-foreground font-mono">
                          {address?.slice(0, 8)}...{address?.slice(-6)}
                        </div>
                      ) : (
                        <div className="text-xs text-red-600">No wallet connected</div>
                      )}
                    </div>
                  </label>

                  <label className="flex items-start gap-3 p-3 border rounded cursor-pointer hover:bg-accent/50">
                    <input
                      type="radio"
                      name="payout-type"
                      checked={useCustomAddress}
                      onChange={() => {
                        setUseCustomAddress(true);
                        setPayoutAddressError('');
                      }}
                      className="mt-1"
                    />
                    <div className="flex-1">
                      <div className="font-medium text-sm">Custom Address</div>
                      <div className="text-xs text-muted-foreground">Enter a different address</div>
                    </div>
                  </label>
                </div>

                {useCustomAddress && (
                  <input
                    type="text"
                    value={customPayoutAddress}
                    onChange={(e) => {
                      setCustomPayoutAddress(e.target.value);
                      setPayoutAddressError('');
                    }}
                    placeholder="0x..."
                    className="w-full mt-2 rounded border bg-background px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#0A74FF] font-mono text-sm"
                  />
                )}

                {payoutAddressError && (
                  <div className="text-sm text-red-600 mt-2">{payoutAddressError}</div>
                )}

                <button
                  onClick={handleSavePayoutAddress}
                  disabled={savingPayoutAddress || (!useCustomAddress && !isConnected)}
                  className="mt-3 inline-flex items-center gap-2 rounded border-2 border-[var(--color-header-accent,#0A74FF)] px-4 py-2 text-sm font-semibold hover:bg-accent"
                >
                  {savingPayoutAddress ? 'Saving...' : 'Save Payout Address'}
                </button>

                {payoutAddress && (
                  <div className="text-xs text-muted-foreground mt-2">
                    Current payout address: <span className="font-mono">{payoutAddress.slice(0, 8)}...{payoutAddress.slice(-6)}</span>
                  </div>
                )}
              </div>

              <button
                disabled
                className="w-full inline-flex items-center justify-center gap-2 rounded border-2 border-gray-400 px-4 py-2 font-semibold text-gray-400 cursor-not-allowed"
              >
                Withdraw Funds
              </button>
            </div>
          </div>

          {/* Success Message */}
          {successMessage && (
            <div className="text-sm text-green-600 bg-green-500/10 border border-green-500/30 rounded px-3 py-2 text-center">
              {successMessage}
            </div>
          )}
      </>
      )}

      {/* App Settings Tab */}
      {activeTab === 'app-settings' && (
        <>
          <SwatchPro />
          
          {/* Button Layout Card */}
          <div className="border rounded-lg p-6 bg-card">
            <h2 className="text-xl font-semibold mb-4 text-center">Button Layout</h2>
            <p className="text-sm text-muted-foreground mb-4 text-center">
              Choose where buttons appear in the app
            </p>
            
            <div className="grid grid-cols-2 gap-3 max-w-md mx-auto">
              <button
                onClick={() => {
                  setButtonLayout('Top');
                  localStorage.setItem('buttonLayout', 'Top');
                  window.dispatchEvent(new CustomEvent('buttonLayoutChanged', { detail: 'Top' }));
                }}
                className={`px-4 py-3 rounded-lg border-2 font-semibold transition-all ${
                  buttonLayout === 'Top'
                    ? 'border-[var(--color-header-accent,#0A74FF)] text-[var(--color-header-accent,#0A74FF)]'
                    : 'border-border hover:border-[var(--color-header-accent,#0A74FF)] hover:bg-accent'
                }`}
                style={buttonLayout === 'Top' ? {
                  backgroundColor: 'color-mix(in srgb, var(--color-header-accent, #0A74FF) 10%, transparent)'
                } : {}}
              >
                Top
              </button>
              
              <button
                onClick={() => {
                  setButtonLayout('Bottom');
                  localStorage.setItem('buttonLayout', 'Bottom');
                  window.dispatchEvent(new CustomEvent('buttonLayoutChanged', { detail: 'Bottom' }));
                }}
                className={`px-4 py-3 rounded-lg border-2 font-semibold transition-all ${
                  buttonLayout === 'Bottom'
                    ? 'border-[var(--color-header-accent,#0A74FF)] text-[var(--color-header-accent,#0A74FF)]'
                    : 'border-border hover:border-[var(--color-header-accent,#0A74FF)] hover:bg-accent'
                }`}
                style={buttonLayout === 'Bottom' ? {
                  backgroundColor: 'color-mix(in srgb, var(--color-header-accent, #0A74FF) 10%, transparent)'
                } : {}}
              >
                Bottom
              </button>
            </div>
            
            <div className="mt-4 text-center text-sm text-muted-foreground">
              Current layout: <span className="font-semibold text-[var(--color-header-accent,#0A74FF)]">{buttonLayout}</span>
            </div>
          </div>
        </>
      )}
      </div>
    </>
  );
}

export default AccountPage;
